import React, { useState, useEffect, useCallback } from 'react';
import { Package, Search, Trash2, Check, X as XIcon, CheckSquare, Square, MinusSquare, Filter, AlertTriangle, RefreshCw, ImagePlus, Image as ImageIcon, Clock } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { getAdminProducts, getAdminProductCounts, deleteProduct, approveProduct, rejectProduct, bulkApproveProducts, bulkRejectProducts, countFilteredProducts, approveAllFiltered, rejectAllFiltered, deleteAllProducts, repriceAllProducts, uploadProductImage, deleteProductImage, getProductImageUrl, updateAdminProduct } from '../../lib/api';
import { Toaster, toast } from 'sonner';
import { DangerConfirmModal } from '../../components/DangerConfirmModal';

// --- Confirmation Modal ---
const ConfirmModal = ({ open, onClose, onConfirm, action, filters, matchedCount, loading, t }) => {
  if (!open) return null;
  const isApprove = action === 'approve';
  const color = isApprove ? '#10B981' : '#EF4444';
  const label = isApprove ? t('adminProducts.approve') : t('adminProducts.reject');

  const activeFilters = [];
  if (filters.status) {
    const statusMap = {
      approved: t('adminProducts.approvedStatus'),
      pending_review: t('adminProducts.pendingReviewStatus'),
      rejected: t('adminProducts.rejectedStatus'),
      import_error: t('adminProducts.importErrorStatus'),
    };
    activeFilters.push({ key: t('adminProducts.status'), val: statusMap[filters.status] || filters.status });
  }
  if (filters.category) activeFilters.push({ key: t('adminProducts.category'), val: filters.category });
  if (filters.search) activeFilters.push({ key: t('adminProducts.searchPlaceholder').split('...')[0], val: `"${filters.search}"` });
  if (activeFilters.length === 0) activeFilters.push({ key: t('adminProducts.filter'), val: t('adminProducts.allProducts') });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="confirm-modal">
      <div className="bg-[#0A0A0A] border border-white/10 p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 mb-4">
          <AlertTriangle className="w-6 h-6" style={{ color }} />
          <h3 className="text-lg font-semibold">{label} {t('adminProducts.allFilteredProducts')}</h3>
        </div>

        <div className="bg-[#050505] border border-white/10 p-4 mb-4 space-y-2">
          {activeFilters.map((f, i) => (
            <div key={i} className="flex items-center justify-between text-sm">
              <span className="text-white/40">{f.key}</span>
              <span className="text-white font-medium capitalize">{f.val}</span>
            </div>
          ))}
          <div className="pt-2 mt-2 border-t border-white/10 flex items-center justify-between">
            <span className="text-white/40 text-sm">{t('adminProducts.totalMatched')}</span>
            <span className="text-xl font-bold font-mono" style={{ color }}>{matchedCount.toLocaleString()}</span>
          </div>
        </div>

        <p className="text-white/50 text-sm mb-6">
          {t('adminProducts.cannotUndo')}
        </p>

        <div className="flex items-center gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-white/50 hover:text-white border border-white/10 transition-colors"
            data-testid="modal-cancel-btn"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={onConfirm}
            disabled={loading || matchedCount === 0}
            className="px-5 py-2 text-sm font-medium text-white disabled:opacity-40 flex items-center gap-2 transition-colors"
            style={{ backgroundColor: color }}
            data-testid="modal-confirm-btn"
          >
            {loading ? t('adminProducts.processing') : `${label} ${matchedCount.toLocaleString()}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export const AdminProducts = () => {
  const { t, language } = useApp();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [gradeFilter, setGradeFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  // Filtered bulk modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalAction, setModalAction] = useState('approve');
  const [filteredCount, setFilteredCount] = useState(0);
  const [countLoading, setCountLoading] = useState(false);

  // Delete All modal
  const [deleteAllOpen, setDeleteAllOpen] = useState(false);
  const [deleteAllLoading, setDeleteAllLoading] = useState(false);

  // Reprice All modal
  const [repriceOpen, setRepriceOpen] = useState(false);
  const [repriceLoading, setRepriceLoading] = useState(false);

  const handleDeleteAll = async ({ confirmation_phrase, acknowledged }) => {
    setDeleteAllLoading(true);
    try {
      const res = await deleteAllProducts({ confirmation_phrase, acknowledged });
      toast.success(res.data.message);
      setDeleteAllOpen(false);
      fetchProducts();
      fetchCounts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete products');
    } finally {
      setDeleteAllLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const res = await getAdminProducts({ 
        status: statusFilter || undefined,
        category: categoryFilter || undefined,
        grade: gradeFilter || undefined,
      });
      setProducts(res.data);
      setSelectedIds(new Set());
    } catch (err) {
      console.error('Failed to fetch products:', err);
      toast.error(t('adminProducts.failedLoadProducts'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [statusFilter, categoryFilter, gradeFilter]);

  // Fetch true counts from dedicated endpoint
  const [categoryCounts, setCategoryCounts] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const fetchCounts = async () => {
    try {
      const res = await getAdminProductCounts({
        status: statusFilter || undefined,
        grade: gradeFilter || undefined,
      });
      setCategoryCounts(res.data.category_counts || {});
      setTotalCount(res.data.total || 0);
    } catch {}
  };
  useEffect(() => {
    fetchCounts();
  }, [statusFilter, gradeFilter]);

  const filteredProducts = products.filter(p => {
    if (!search) return true;
    const term = search.toLowerCase();
    return (
      (p.unique_id || p.product_id || p.supplier_sku || '').toLowerCase().includes(term) ||
      p.model?.toLowerCase().includes(term) ||
      p.oem?.toLowerCase().includes(term)
    );
  });

  const currentFilters = useCallback(() => {
    const f = {};
    if (statusFilter) f.status = statusFilter;
    if (categoryFilter) f.category = categoryFilter;
    if (search) f.search = search;
    return f;
  }, [statusFilter, categoryFilter, search]);

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredProducts.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredProducts.map(p => p.id)));
    }
  };

  const clearSelection = () => setSelectedIds(new Set());
  const isAllSelected = filteredProducts.length > 0 && selectedIds.size === filteredProducts.length;
  const isPartialSelected = selectedIds.size > 0 && selectedIds.size < filteredProducts.length;

  const handleApprove = async (id) => {
    try {
      await approveProduct(id);
      toast.success(t('adminProducts.productApproved'));
      fetchProducts(); fetchCounts();
    } catch (err) {
      toast.error(t('adminProducts.failedApprove'));
    }
  };

  const handleReject = async (id) => {
    try {
      await rejectProduct(id);
      toast.success(t('adminProducts.productRejected'));
      fetchProducts(); fetchCounts();
    } catch (err) {
      toast.error(t('adminProducts.failedReject'));
    }
  };

  const handleReconfirmAvailability = async (id) => {
    try {
      await updateAdminProduct(id, {
        supplier_availability_date: new Date().toISOString(),
      });
      toast.success(language === 'es' ? 'Disponibilidad reconfirmada' : 'Availability reconfirmed');
      fetchProducts();
    } catch (err) {
      toast.error(language === 'es' ? 'Error al reconfirmar' : 'Failed to reconfirm');
    }
  };

  const handleClearAvailability = async (id) => {
    try {
      await updateAdminProduct(id, {
        supplier_availability_date: null,
      });
      toast.success(language === 'es' ? 'Fecha de disponibilidad eliminada' : 'Availability date cleared');
      fetchProducts();
    } catch (err) {
      toast.error(language === 'es' ? 'Error al limpiar' : 'Failed to clear');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('adminProducts.confirmDelete'))) return;
    try {
      await deleteProduct(id);
      toast.success(t('adminProducts.productDeleted'));
      fetchProducts(); fetchCounts();
    } catch (err) {
      toast.error(t('adminProducts.failedDelete'));
    }
  };

  const handleBulkApprove = async () => {
    const ids = [...selectedIds];
    setBulkLoading(true);
    try {
      const res = await bulkApproveProducts(ids);
      const { approved, failed } = res.data;
      if (failed > 0) {
        toast.warning(`${t('adminProducts.approve')}: ${approved}, ${t('adminImport.errors')}: ${failed}`);
      } else {
        toast.success(`${t('adminProducts.approve')}: ${approved}`);
      }
      fetchProducts(); fetchCounts();
    } catch (err) {
      toast.error(t('adminProducts.bulkApproveFailed'));
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkReject = async () => {
    if (!window.confirm(`${t('adminProducts.reject')} ${selectedIds.size} ${t('adminProducts.selected')}?`)) return;
    const ids = [...selectedIds];
    setBulkLoading(true);
    try {
      const res = await bulkRejectProducts(ids);
      const { rejected, failed } = res.data;
      if (failed > 0) {
        toast.warning(`${t('adminProducts.reject')}: ${rejected}, ${t('adminImport.errors')}: ${failed}`);
      } else {
        toast.success(`${t('adminProducts.reject')}: ${rejected}`);
      }
      fetchProducts(); fetchCounts();
    } catch (err) {
      toast.error(t('adminProducts.bulkRejectFailed'));
    } finally {
      setBulkLoading(false);
    }
  };

  const openFilteredModal = async (action) => {
    setModalAction(action);
    setCountLoading(true);
    setModalOpen(true);
    try {
      const res = await countFilteredProducts(currentFilters());
      setFilteredCount(res.data.count);
    } catch (err) {
      toast.error(t('adminProducts.countFailed'));
      setFilteredCount(0);
    } finally {
      setCountLoading(false);
    }
  };

  const handleFilteredConfirm = async () => {
    setBulkLoading(true);
    try {
      const fn = modalAction === 'approve' ? approveAllFiltered : rejectAllFiltered;
      const res = await fn(currentFilters());
      const { updated, failed } = res.data;
      const verb = modalAction === 'approve' ? t('adminProducts.approve') : t('adminProducts.reject');
      if (failed > 0) {
        toast.warning(`${verb}: ${updated}, ${t('adminImport.errors')}: ${failed}`);
      } else {
        toast.success(`${verb}: ${updated}`);
      }
      setModalOpen(false);
      fetchProducts(); fetchCounts();
    } catch (err) {
      toast.error(t('adminProducts.filteredActionFailed'));
    } finally {
      setBulkLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      approved: 'status-approved',
      pending_review: 'status-pending',
      rejected: 'status-rejected',
      import_error: 'status-rejected',
    };
    return badges[status] || 'status-pending';
  };

  const getStatusLabel = (status) => {
    const map = {
      approved: t('adminProducts.approvedStatus'),
      pending_review: t('adminProducts.pendingReviewStatus'),
      rejected: t('adminProducts.rejectedStatus'),
      import_error: t('adminProducts.importErrorStatus'),
    };
    return map[status] || status;
  };

  return (
    <div className="p-6 lg:p-8" data-testid="admin-products">
      <Toaster position="top-right" theme="dark" />
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Package className="w-8 h-8 text-[#00D4FF]" />
            {t('adminProducts.title')}
          </h1>
          <p className="text-white/60 mt-1">{totalCount} {t('adminProducts.totalProducts')}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
          <input
            type="text"
            placeholder={t('adminProducts.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-[#0A0A0A] border border-white/10 focus:border-[#00D4FF]"
            data-testid="product-search"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-3 bg-[#0A0A0A] border border-white/10 focus:border-[#00D4FF] min-w-[160px]"
          data-testid="status-filter"
        >
          <option value="">{t('adminProducts.allStatuses')}</option>
          <option value="approved">{t('adminProducts.approvedStatus')}</option>
          <option value="pending_review">{t('adminProducts.pendingReviewStatus')}</option>
          <option value="rejected">{t('adminProducts.rejectedStatus')}</option>
          <option value="import_error">{t('adminProducts.importErrorStatus')}</option>
        </select>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="px-4 py-3 bg-[#0A0A0A] border border-white/10 focus:border-[#00D4FF] min-w-[140px]"
          data-testid="category-filter"
        >
          <option value="">{t('adminProducts.allCategories')}</option>
          {['PC', 'NBK', 'AIO', 'Workstations', 'LCD', 'Apple'].map(c => (
            <option key={c} value={c}>{c}{categoryCounts[c] ? ` (${categoryCounts[c]})` : ''}</option>
          ))}
        </select>
        <select
          value={gradeFilter}
          onChange={(e) => setGradeFilter(e.target.value)}
          className="px-4 py-3 bg-[#0A0A0A] border border-white/10 focus:border-[#00D4FF] min-w-[130px]"
          data-testid="grade-filter"
        >
          <option value="">{t('adminProducts.allGrades')}</option>
          <option value="A+">A+</option>
          <option value="A">A</option>
          <option value="B">B</option>
          <option value="C">C</option>
        </select>
      </div>

      {/* Approve/Reject All Filtered bar */}
      <div className="flex items-center gap-2 mb-6 flex-wrap" data-testid="filtered-actions-bar">
        <Filter className="w-4 h-4 text-white/30" />
        <span className="text-xs text-white/30 uppercase tracking-wider mr-2">
          {filteredProducts.length} {t('adminProducts.shown')}
        </span>
        <button
          onClick={() => openFilteredModal('approve')}
          disabled={filteredProducts.length === 0}
          className="px-3 py-1.5 text-xs font-medium uppercase tracking-wider border border-[#10B981]/30 text-[#10B981] hover:bg-[#10B981]/10 disabled:opacity-20 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
          data-testid="approve-all-filtered-btn"
        >
          <Check className="w-3.5 h-3.5" />
          {t('adminProducts.approveAllFiltered')}
        </button>
        <button
          onClick={() => openFilteredModal('reject')}
          disabled={filteredProducts.length === 0}
          className="px-3 py-1.5 text-xs font-medium uppercase tracking-wider border border-[#EF4444]/30 text-[#EF4444] hover:bg-[#EF4444]/10 disabled:opacity-20 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
          data-testid="reject-all-filtered-btn"
        >
          <XIcon className="w-3.5 h-3.5" />
          {t('adminProducts.rejectAllFiltered')}
        </button>
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => setRepriceOpen(true)}
            disabled={products.length === 0}
            className="px-3 py-1.5 text-xs font-medium uppercase tracking-wider border border-[#00D4FF]/30 text-[#00D4FF] hover:bg-[#00D4FF]/10 disabled:opacity-20 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
            data-testid="reprice-all-btn"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            {language === 'es' ? 'Recalcular Precios' : 'Reprice All'}
          </button>
          <button
            onClick={() => setDeleteAllOpen(true)}
            disabled={products.length === 0}
            className="px-3 py-1.5 text-xs font-medium uppercase tracking-wider bg-[#EF4444]/10 border border-[#EF4444]/30 text-[#EF4444] hover:bg-[#EF4444]/20 disabled:opacity-20 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
            data-testid="delete-all-btn"
          >
            <Trash2 className="w-3.5 h-3.5" />
            {t('adminProducts.deleteAll')}
          </button>
        </div>
      </div>

      {/* Reprice All Confirmation Modal */}
      <DangerConfirmModal
        open={repriceOpen}
        onClose={() => !repriceLoading && setRepriceOpen(false)}
        onConfirm={async ({ confirmation_phrase, acknowledged }) => {
          setRepriceLoading(true);
          try {
            const res = await repriceAllProducts({ confirmation_phrase, acknowledged });
            toast.success(res.data.message);
            setRepriceOpen(false);
            fetchProducts();
          } catch (err) {
            toast.error(err?.response?.data?.detail || 'Reprice failed');
          } finally { setRepriceLoading(false); }
        }}
        title="Run Bulk Product Update"
        description={`This will recalculate sale prices for all ${products.length} product(s) using tiered markup rules. Manual and locked price overrides will be skipped.`}
        confirmPhrase="RUN BULK PRODUCT UPDATE"
        loading={repriceLoading}
      />

      {/* Delete All Confirmation Modal */}
      <DangerConfirmModal
        open={deleteAllOpen}
        onClose={() => !deleteAllLoading && setDeleteAllOpen(false)}
        onConfirm={handleDeleteAll}
        title="Delete All Products"
        description={`This will permanently delete all ${products.length} product(s) from the catalog. This action cannot be undone.`}
        confirmPhrase="DELETE ALL PRODUCTS"
        loading={deleteAllLoading}
      />

      {/* Sticky Bulk Action Bar */}
      {selectedIds.size > 0 && (
        <div
          className="sticky top-0 z-30 flex items-center justify-between gap-4 px-5 py-3 mb-4 bg-[#0A0A0A]/95 backdrop-blur-md border border-[#00D4FF]/30"
          data-testid="bulk-action-bar"
        >
          <span className="text-sm font-mono">
            <span className="text-[#00D4FF] font-bold">{selectedIds.size}</span>
            <span className="text-white/50 ml-1">{t('adminProducts.selected')}</span>
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={handleBulkApprove}
              disabled={bulkLoading}
              className="px-4 py-2 text-xs font-medium uppercase tracking-wider bg-[#10B981] text-black hover:bg-[#10B981]/80 disabled:opacity-40 flex items-center gap-2 transition-colors"
              data-testid="bulk-approve-btn"
            >
              <Check className="w-4 h-4" />
              {t('adminProducts.approveSelected')} ({selectedIds.size})
            </button>
            <button
              onClick={handleBulkReject}
              disabled={bulkLoading}
              className="px-4 py-2 text-xs font-medium uppercase tracking-wider bg-[#EF4444] text-white hover:bg-[#EF4444]/80 disabled:opacity-40 flex items-center gap-2 transition-colors"
              data-testid="bulk-reject-btn"
            >
              <XIcon className="w-4 h-4" />
              {t('adminProducts.rejectSelected')} ({selectedIds.size})
            </button>
            <button
              onClick={clearSelection}
              className="px-4 py-2 text-xs font-medium uppercase tracking-wider text-white/50 hover:text-white border border-white/10 transition-colors"
              data-testid="clear-selection-btn"
            >
              {t('adminProducts.clear')}
            </button>
          </div>
        </div>
      )}

      {/* Products Table */}
      {loading ? (
        <div className="card-dark p-8 text-center">
          <div className="animate-pulse text-white/40">{t('adminProducts.loadingProducts')}</div>
        </div>
      ) : filteredProducts.length === 0 ? (
        <div className="card-dark p-8 text-center">
          <p className="text-white/40">{t('adminProducts.noProducts')}</p>
        </div>
      ) : (
        <div className="card-dark overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full table-terminal">
              <thead>
                <tr>
                  <th className="w-10">
                    <button
                      onClick={toggleSelectAll}
                      className="p-1 text-white/50 hover:text-[#00D4FF] transition-colors"
                      data-testid="select-all-checkbox"
                    >
                      {isAllSelected ? (
                        <CheckSquare className="w-5 h-5 text-[#00D4FF]" />
                      ) : isPartialSelected ? (
                        <MinusSquare className="w-5 h-5 text-[#00D4FF]" />
                      ) : (
                        <Square className="w-5 h-5" />
                      )}
                    </button>
                  </th>
                  <th className="text-left">{t('adminProducts.sku')}</th>
                  <th className="w-14 text-center">{language === 'es' ? 'Img' : 'Img'}</th>
                  <th className="text-left">{t('adminProducts.brand')}</th>
                  <th className="text-left">{t('adminProducts.model')}</th>
                  <th className="text-left">{t('adminProducts.category')}</th>
                  <th className="text-right">{t('adminProducts.costPrice')}</th>
                  <th className="text-right">{t('adminProducts.salePrice')}</th>
                  <th className="text-center">{language === 'es' ? 'Markup' : 'Markup'}</th>
                  <th className="text-center">{t('adminProducts.grade')}</th>
                  <th className="text-center">{language === 'es' ? 'Disponib.' : 'Avail.'}</th>
                  <th className="text-center">{t('adminProducts.status')}</th>
                  <th className="text-right">{t('adminProducts.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map((product) => {
                  const isSelected = selectedIds.has(product.id);
                  return (
                    <tr
                      key={product.id}
                      className={isSelected ? 'bg-[#00D4FF]/5' : ''}
                      data-testid={`product-row-${product.id}`}
                    >
                      <td>
                        <button
                          onClick={() => toggleSelect(product.id)}
                          className="p-1 text-white/30 hover:text-[#00D4FF] transition-colors"
                          data-testid={`checkbox-${product.id}`}
                        >
                          {isSelected ? (
                            <CheckSquare className="w-5 h-5 text-[#00D4FF]" />
                          ) : (
                            <Square className="w-5 h-5" />
                          )}
                        </button>
                      </td>
                      <td className="font-mono text-xs text-white/50">{product.unique_id || product.product_id || product.supplier_sku || '—'}</td>
                      <td className="text-center">
                        {product.image_url ? (
                          <div className="group relative inline-block">
                            <img src={getProductImageUrl(product.id)} alt="" className="w-8 h-8 object-cover border border-white/10" />
                            <button
                              onClick={async () => {
                                try { await deleteProductImage(product.id); toast.success('Image removed'); fetchProducts(); }
                                catch { toast.error('Failed to remove image'); }
                              }}
                              className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[8px] rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                              data-testid={`delete-image-${product.id}`}
                            >x</button>
                          </div>
                        ) : (
                          <label className="cursor-pointer inline-flex items-center justify-center w-8 h-8 border border-dashed border-white/20 hover:border-[#00D4FF]/50 transition-colors" data-testid={`upload-image-${product.id}`}>
                            <ImagePlus className="w-3.5 h-3.5 text-white/30" />
                            <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              try { await uploadProductImage(product.id, file); toast.success('Image uploaded'); fetchProducts(); }
                              catch { toast.error('Failed to upload image'); }
                              e.target.value = '';
                            }} />
                          </label>
                        )}
                      </td>
                      <td className="text-[#00D4FF]">{product.oem}</td>
                      <td className="max-w-[200px] truncate">{product.model}</td>
                      <td className="text-white/60">{product.category}</td>
                      <td className="text-right font-mono text-white/50">${product.cost_price?.toLocaleString()}</td>
                      <td className="text-right font-mono text-[#00D4FF]">
                        ${product.sale_price?.toLocaleString()}
                        {product.sale_price_override && <span className="text-[#F59E0B] text-[10px] ml-1">OVR</span>}
                      </td>
                      <td className="text-center text-xs text-white/40">
                        {product.markup_pct ? `${(product.markup_pct * 100).toFixed(0)}%` : '—'}
                      </td>
                      <td className="text-center">
                        {product.grade && (
                          <span className={`px-2 py-1 text-xs font-bold text-white ${
                            product.grade?.includes('A') ? 'grade-a' :
                            product.grade?.includes('B') ? 'grade-b' : 'grade-c'
                          }`}>
                            {product.grade}
                          </span>
                        )}
                      </td>
                      <td className="text-center" data-testid={`avail-admin-${product.id}`}>
                        {(() => {
                          const sad = product.supplier_availability_date;
                          if (!sad) {
                            return (
                              <div className="flex flex-col items-center gap-1">
                                <span className="text-[#F59E0B] text-[10px]">{language === 'es' ? 'Sin fecha' : 'No date'}</span>
                                <button
                                  onClick={() => handleReconfirmAvailability(product.id)}
                                  className="text-[9px] text-[#00D4FF] hover:underline"
                                  data-testid={`reconfirm-${product.id}`}
                                  title={language === 'es' ? 'Confirmar disponibilidad ahora' : 'Confirm availability now'}
                                >
                                  <Clock className="w-3 h-3 inline mr-0.5" />
                                  {language === 'es' ? 'Confirmar' : 'Confirm'}
                                </button>
                              </div>
                            );
                          }
                          const daysSince = Math.floor((Date.now() - new Date(sad).getTime()) / (1000 * 60 * 60 * 24));
                          const isFresh = daysSince <= 14;
                          const prov = product.supplier_availability_provenance;
                          const provLabel = prov === 'admin-reconfirmed' ? (language === 'es' ? 'admin' : 'admin')
                            : prov === 'explicit-supplier-date' ? (language === 'es' ? 'proveedor' : 'supplier')
                            : (language === 'es' ? 'importación' : 'import');
                          return (
                            <div className="flex flex-col items-center gap-0.5">
                              <span className={`text-[10px] font-medium ${isFresh ? 'text-[#10B981]' : 'text-[#F59E0B]'}`}>
                                {daysSince}d {language === 'es' ? 'atrás' : 'ago'}
                              </span>
                              <span className="text-[8px] text-white/30">{provLabel}</span>
                              <div className="flex gap-1">
                                <button
                                  onClick={() => handleReconfirmAvailability(product.id)}
                                  className="text-[8px] text-[#00D4FF] hover:underline"
                                  data-testid={`reconfirm-${product.id}`}
                                  title={language === 'es' ? 'Reconfirmar ahora' : 'Reconfirm now'}
                                >
                                  <RefreshCw className="w-2.5 h-2.5" />
                                </button>
                                <button
                                  onClick={() => handleClearAvailability(product.id)}
                                  className="text-[8px] text-white/30 hover:text-[#EF4444]"
                                  data-testid={`clear-avail-${product.id}`}
                                  title={language === 'es' ? 'Limpiar fecha' : 'Clear date'}
                                >
                                  <XIcon className="w-2.5 h-2.5" />
                                </button>
                              </div>
                            </div>
                          );
                        })()}
                      </td>
                      <td className="text-center">
                        <span className={`status-badge ${getStatusBadge(product.status)}`}>
                          {getStatusLabel(product.status)}
                        </span>
                      </td>
                      <td className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {product.status === 'pending_review' && (
                            <>
                              <button
                                onClick={() => handleApprove(product.id)}
                                className="p-2 text-[#10B981] hover:bg-[#10B981]/10"
                                title={t('adminProducts.approve')}
                                data-testid={`approve-${product.id}`}
                              >
                                <Check className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleReject(product.id)}
                                className="p-2 text-[#EF4444] hover:bg-[#EF4444]/10"
                                title={t('adminProducts.reject')}
                                data-testid={`reject-${product.id}`}
                              >
                                <XIcon className="w-4 h-4" />
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => handleDelete(product.id)}
                            className="p-2 text-white/40 hover:text-red-400"
                            title={t('adminProducts.delete')}
                            data-testid={`delete-${product.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={handleFilteredConfirm}
        action={modalAction}
        filters={currentFilters()}
        matchedCount={countLoading ? 0 : filteredCount}
        loading={bulkLoading || countLoading}
        t={t}
      />
    </div>
  );
};
